import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/common/Buttons/Button";
import AnimationPopup from "@/components/common/Popups/AnimationPopup";
import { servicesOrchestratorValues } from "@/config/services";
import useNavigationHelpers from "@/hooks/useNavigationHelpers";
import { instructionSearchParamsSchema } from "@/routes/-search-params";
import type { InstructionsConfig } from "@/services/types";
import { useStore } from "@/store";

const steps: Array<{
	key: string;
	configKey: keyof InstructionsConfig;
	animation: string;
	icon: string;
	titleKey: string;
	subtitleKey: string;
	ctaKey: string;
	popup: { titleKey: string; bodyKey: string };
}> = [
	{
		key: "remove-screen-protector",
		configKey: "removeScreenProtector",
		animation: "/images/instructions/screen-protector-animated.svg",
		icon: `${servicesOrchestratorValues.urls.cdn}/vision/instructions/remove-screen-protector.svg`,
		titleKey: "instructions.Remove screen protector",
		subtitleKey: "instructions.Take off the screen protector to get the best possible offer.",
		ctaKey: "btn.Continue",
		popup: {
			titleKey: "info pop ups.Removing screen protector",
			bodyKey:
				"info pop ups.Screen protectors can hide or distort scratches, cracks, or display imperfections. To accurately assess the screen\u2019s real condition, it must be evaluated without a protector. Keeping it on may lead to an inaccurate assessment and a lower offer.",
		},
	},
	{
		key: "accessories-removed",
		configKey: "removeAccessories",
		animation: "/images/instructions/phone-case-animation.svg",
		icon: `${servicesOrchestratorValues.urls.cdn}/vision/instructions/accessories-removed.svg`,
		titleKey: "instructions.Remove all accessories",
		subtitleKey: "instructions.Remove any case, stickers, or skins to get an accurate offer.",
		ctaKey: "btn.I removed them.",
		popup: {
			titleKey: "info pop ups.Removing all accessories",
			bodyKey:
				"info pop ups.Cases, stickers, skins, or other accessories can block parts of the device and interfere with the scan. If any accessories remain on the device, VisionAI may not be able to complete the evaluation and will ask you to repeat the cosmetic scan after removing them.",
		},
	},
	{
		key: "device-cleaned",
		configKey: "cleanDevice",
		animation: "/images/instructions/phone-cleaning-animated.svg",
		icon: `${servicesOrchestratorValues.urls.cdn}/vision/instructions/device-cleaned.svg`,
		titleKey: "instructions.Clean the device",
		subtitleKey: "instructions.Thoroughly clean the device with a soft cloth to get the best offer.",
		ctaKey: "btn.I cleaned the device.",
		popup: {
			titleKey: "info pop ups.Cleaning the device",
			bodyKey:
				"info pop ups.VisionAI analyzes the device\u2019s surface in detail. Dust, fingerprints, or residue can be mistaken for scratches or cosmetic damage. This may negatively affect the evaluation and result in a lower offer than your device actually deserves.",
		},
	},
];

export const Route = createFileRoute("/$locale/$partner/_app/_card/instructions")({
	validateSearch: (search) => instructionSearchParamsSchema.parse(search),
	component: Instructions,
});

type InstructionsContentProps = {
	instructionStep: number;
	onInstructionStepChange: (newStep: number) => void;
};

function Instructions() {
	const { instructionStep } = Route.useSearch();
	const navigate = Route.useNavigate();

	const updateStep = useCallback(
		(newStep: number) => {
			navigate({
				search: (previousSearch) => ({
					...previousSearch,
					instructionStep: newStep,
				}),
			});
		},
		[navigate],
	);

	return (
		<InstructionsContent
			instructionStep={instructionStep ?? 0}
			onInstructionStepChange={updateStep}
		/>
	);
}

function InstructionsContent({ instructionStep, onInstructionStepChange }: InstructionsContentProps) {
	const { t } = useTranslation();
	const { goToNextStep } = useNavigationHelpers();
	const [showPopup, setShowPopup] = useState(false);
	const instructionsConfig = useStore((state) => state.config?.pagesConfig.instructions);

	// Only the steps enabled in the partner config are shown. A missing config
	// (older partner configs without the field) keeps every step visible.
	const visibleSteps = useMemo(
		() => steps.filter((step) => instructionsConfig?.[step.configKey] ?? true),
		[instructionsConfig],
	);

	// When a partner disables every preparation step, skip the screen entirely.
	const hasNoSteps = visibleSteps.length === 0;
	useEffect(() => {
		if (hasNoSteps) goToNextStep(undefined, { immediate: true });
	}, [hasNoSteps, goToNextStep]);

	if (hasNoSteps) return null;

	const currentStep = Math.min(Math.max(instructionStep, 0), visibleSteps.length - 1);

	const step = visibleSteps[currentStep]!;
	const isLastStep = currentStep === visibleSteps.length - 1;

	const handleNext = () => {
		if (isLastStep) {
			goToNextStep();
		} else {
			onInstructionStepChange(currentStep + 1);
		}
	};

	return (
		<div className="h-full w-full flex flex-col pt-6 sm:pt-10 relative">
			{/* Title + Subtitle - left aligned */}
			<div className="px-2">
				<h3 className="font-bold leading-tight text-2xl">{t(step.titleKey)}</h3>
				<p className="mt-2 text-xl sm:text-base">{t(step.subtitleKey)}</p>
			</div>
			{/* Animated SVG - centered */}
			<div className="flex-1 flex items-center justify-center">
				<div className="w-[22.375rem] h-full sm:w-96 sm:h-96 relative">
					<img
						src={step.animation}
						alt={step.key}
						className="w-full h-full object-contain"
					/>
				</div>
			</div>
			{/* CTA + Why this matters */}
			<div className="flex flex-col items-center gap-4 pb-6 sm:pb-10 pt-4 px-2">
				<div className="w-full [&>button]:w-full">
					<Button
						label={t(step.ctaKey)}
						onClick={handleNext}
					/>
				</div>
				<button
					className="text-sm font-semibold"
					onClick={() => setShowPopup(true)}
				>
					{t("btn.Why this matters")}
				</button>
			</div>
			{/* Detail popup with icon */}
			<AnimationPopup
				showModal={showPopup}
				setShowModal={setShowPopup}
				content={
					<div className="flex flex-col items-center max-w-lg px-2 pt-4">
						<div className="w-20 h-20 sm:w-24 sm:h-24 relative mb-6">
							<img
								src={step.icon}
								alt={step.key}
								className="w-full h-full object-contain"
							/>
						</div>
						<h4 className="font-bold mb-4 text-xl text-center">{t(step.popup.titleKey)}</h4>
						<p
							className="text-base text-left"
							dangerouslySetInnerHTML={{ __html: t(step.popup.bodyKey).replace(/\n/g, "<br/>") }}
						/>
					</div>
				}
			/>
		</div>
	);
}
